/**
 * @file App shell event listener setup for navigation, forms, and modals.
 */

import { handleLogout } from "../auth/authController.js";
import {
    initNotificationListeners,
    toggleShade,
} from "../notifications/notificationController.js";
import { handleFormSubmit } from "../../shared/forms/modalSubmitRouter.js";
import { openAssetLogForm } from "../../domains/assets/assetLogForms.js";
import {
    openDrugInventoryForm,
    openDrugRoleForm,
} from "../../domains/drugs/drugAdminModals.js";
import { openChecklistTemplateForm } from "../../domains/settings/checklistTemplateForm.js";
import { openUserInviteForm } from "../../domains/settings/userFormModal.js";
import { formItemData } from "../../shared/forms/formModalState.js";
import {
    closeForm,
} from "../../shared/forms/modalShell.js";
import {
    isParentFormType,
} from "../../shared/forms/parentFormSubmit.js";
import { openParentForm } from "../../shared/forms/parentFormModal.js";
import { openLabsCreateFlow } from "../../domains/labs/labsFormFields.js";
import { initBoardControlBarListeners } from "../../shared/board/boardControls.js";
import { navigate } from "../routing/navigationController.js";
import { openMaintenanceCreateFlow } from "../../domains/maintenance/maintenanceFormFields.js";
import { openRecallQuickAddModal } from "../../domains/recalls/recallsQuickAddModal.js";
import { renderRecallsView } from "../../domains/recalls/recallsView.js";
import { state } from "../state/state.js";

const confirmModal = document.getElementById('confirm-modal');
const cancelConfirmBtn = document.getElementById('cancel-confirm-btn');
const filterModal = document.getElementById('filter-modal');

function openNonParentForm(formType) {
    switch (formType) {
        case 'asset_service':
        case 'asset_issue':
            openAssetLogForm(formType);
            break;
        case 'user_invite':
            openUserInviteForm();
            break;
        case 'drug_inventory':
            openDrugInventoryForm();
            break;
        case 'drug_role':
            openDrugRoleForm();
            break;
        case 'checklist_template':
            openChecklistTemplateForm();
            break;
        default:
            console.error(`Unsupported non-parent form type: ${formType}`);
            break;
    }
}

function openRecallQuickAddFromShell() {
    openRecallQuickAddModal({
        onSuccess: () => {
            if (state.currentView === "recalls") {
                renderRecallsView();
            }
        },
    });
}

/**
 * Sets up primary event listeners for the application shell after sign-in.
 */
export function setupAppEventListeners() {
    const navContainer = document.querySelector('nav ul');
    if (navContainer) {
        navContainer.addEventListener('click', (e) => {
            if (e.target.matches('.nav-link')) {
                navigate(e.target.dataset.view);
            }
        });
    }

    const mainContent = document.getElementById('main-content');
    if (mainContent) {
        mainContent.addEventListener('click', (e) => {
            if (e.target.closest('[data-mobile-recall-quick-add]')) {
                openRecallQuickAddFromShell();
                return;
            }
            if (e.target.closest('[data-mobile-route-notifications]')) {
                toggleShade();
                return;
            }

            const addButton = e.target.closest('.add-item-btn');
            if (addButton) {
                const formType = addButton.dataset.type;
                if (formType === "maintenance") {
                    openMaintenanceCreateFlow();
                    return;
                }
                if (formType === "lab") {
                    openLabsCreateFlow();
                    return;
                }
                if (isParentFormType(formType)) {
                    openParentForm(formType);
                } else {
                    openNonParentForm(formType);
                }
            }
        });
    }

    const cancelFormBtn = document.getElementById('cancel-form-btn');
    const formModalElement = document.getElementById('form-modal');
    const itemForm = document.getElementById('item-form');

    if (cancelFormBtn) {cancelFormBtn.onclick = closeForm;}
    if (formModalElement) {formModalElement.onclick = null;}
    if (itemForm) {itemForm.onsubmit = handleFormSubmit;}

    initBoardControlBarListeners();

    const headerRight = document.getElementById('header-right');
    if (headerRight) {
        headerRight.onclick = e => {
            const mobileMenu = headerRight.querySelector('#mobile-menu-panel');
            const mobileMenuToggle = e.target.closest('#mobile-menu-toggle');
            if (mobileMenuToggle && mobileMenu) {
                const shouldOpen = mobileMenu.classList.contains('hidden');
                mobileMenu.classList.toggle('hidden', !shouldOpen);
                mobileMenuToggle.setAttribute('aria-expanded', String(shouldOpen));
                mobileMenuToggle.querySelector('.material-symbols-outlined').textContent =
                    shouldOpen ? 'close' : 'menu';
                return;
            }

            const mobileNavLink = e.target.closest('[data-mobile-view]');
            if (mobileNavLink) {
                mobileMenu?.classList.add('hidden');
                headerRight.querySelector('#mobile-menu-toggle')
                    ?.setAttribute('aria-expanded', 'false');
                headerRight.querySelector('#mobile-menu-toggle .material-symbols-outlined')
                    ?.replaceChildren(document.createTextNode('menu'));
                navigate(mobileNavLink.dataset.mobileView);
                return;
            }

            if (e.target.closest('#settings-btn')) {
                navigate('settings');
            }
            if (e.target.closest('#mobile-recall-quick-add-btn')) {
                mobileMenu?.classList.add('hidden');
                openRecallQuickAddFromShell();
                return;
            }
            if (e.target.closest('#mobile-calendar-btn')) {
                mobileMenu?.classList.add('hidden');
                headerRight.querySelector('#mobile-menu-toggle')
                    ?.setAttribute('aria-expanded', 'false');
                headerRight.querySelector('#mobile-menu-toggle .material-symbols-outlined')
                    ?.replaceChildren(document.createTextNode('menu'));
                navigate('calendar');
                return;
            }
            if (e.target.closest('#mobile-settings-btn')) {
                mobileMenu?.classList.add('hidden');
                headerRight.querySelector('#mobile-menu-toggle')
                    ?.setAttribute('aria-expanded', 'false');
                headerRight.querySelector('#mobile-menu-toggle .material-symbols-outlined')
                    ?.replaceChildren(document.createTextNode('menu'));
                navigate('settings');
                return;
            }
            if (e.target.closest('#logout-btn')) {
                handleLogout();
            }
            if (e.target.closest('#mobile-logout-btn')) {
                mobileMenu?.classList.add('hidden');
                handleLogout();
            }
        };
    }

    document.addEventListener('click', (e) => {
        const mobileMenuRoot = document.getElementById('mobile-app-menu');
        if (!mobileMenuRoot || mobileMenuRoot.contains(e.target)) {return;}
        const mobileMenu = document.getElementById('mobile-menu-panel');
        const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        if (!mobileMenu || mobileMenu.classList.contains('hidden')) {return;}
        mobileMenu.classList.add('hidden');
        mobileMenuToggle?.setAttribute('aria-expanded', 'false');
        mobileMenuToggle?.querySelector('.material-symbols-outlined')
            ?.replaceChildren(document.createTextNode('menu'));
    });

    const header = document.querySelector('header');
    if (header) {
        header.addEventListener('click', (e) => {
            if (e.target.closest('#recall-quick-add-btn')) {
                openRecallQuickAddFromShell();
                return;
            }
            if (e.target.closest('#calendar-btn')) {
                navigate('calendar');
                return;
            }
            if (e.target.closest('.notification-bell-btn')) {
                toggleShade();
            }
        });
    }
    initNotificationListeners();

    if (cancelConfirmBtn) {cancelConfirmBtn.onclick = () => confirmModal?.classList.remove('flex');}
    if (confirmModal) {confirmModal.onclick = null;}

    document.addEventListener('click', (e) => {
        if (filterModal && !filterModal.contains(e.target) && !e.target.closest('.filter-btn')) {
            filterModal.classList.add('hidden');
        }
    });

    const formFieldsContainer = document.getElementById('form-fields');
    if (formFieldsContainer) {
        formFieldsContainer.addEventListener('click', e => {
            if (e.target.matches('.multi-select-choice-tag')) {
                const button = e.target;
                const field = button.dataset.field;
                const value = button.dataset.value;

                if (!formItemData[field]) {formItemData[field] = [];}

                const index = formItemData[field].indexOf(value);
                if (index > -1) {
                    formItemData[field].splice(index, 1);
                    button.classList.replace('bg-[var(--dfc-brand-primary)]', 'bg-gray-200');
                    button.classList.replace('text-[var(--dfc-brand-primary-contrast)]', 'text-gray-600');
                } else {
                    formItemData[field].push(value);
                    button.classList.replace('bg-gray-200', 'bg-[var(--dfc-brand-primary)]');
                    button.classList.replace('text-gray-600', 'text-[var(--dfc-brand-primary-contrast)]');
                }
            }
        });
    } else {
        console.error("Form fields container (#form-fields) not found.");
    }
}
